import type { FormikValues } from "formik";
import { useField } from "formik";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useState } from "react";

import type { SliderProps } from "@citrine/atoms/slider";
import { Slider as BaseSlider } from "@citrine/atoms/slider";
import { sleep } from "@citrine/utils/helpers";
import type { Paths } from "@citrine/utils/types";

import { useValidateOnChange } from "./utils";

export interface FormSliderProps<Shape extends FormikValues> extends Omit<
  SliderProps,
  "name" | "value"
> {
  validateOnChange?: boolean;
  name: Paths<Shape, number>;
}

export function Slider<Shape extends FormikValues>({
  name,
  validateOnChange: validateOnChangeProp,
  ...props
}: FormSliderProps<Shape>) {
  const [field, , helpers] = useField({
    name,
  });

  const [internalValue, setInternalValue] = useState(field.value);

  // If the form value changes from outside, i.e. via TextFields, update the
  // internal value.
  useEffect(() => {
    // It appears that if you go from a number i.e. 100 straight to NaN, the Slider
    // does not change. It registers that the value is NaN, but the Slider Thumb
    // and Track do not go away. This manifests itself if you are resetting the field
    // back to its original value if the original value is NaN.
    //
    // When used in conjuction with a TextField, i.e. our SliderWithInputs component,
    // clearing the value on the TextField but keeping focus actually sets `field.value`
    // to "" - blurring the TextField then sets `field.value` to `NaN`, which is why
    // the TextField-Slider interaction worked without coercing `NaN` to "".
    //
    // MUI actually warns if you give it NaN and suggests that you use "" instead,
    // but also warns if you give it a string instead of a number ¯\_(ツ)_/¯
    setInternalValue(isNaN(field.value) ? "" : field.value);
  }, [field.value]);

  const onChange = useCallback(
    (_: Event, value: number | number[]) => setInternalValue(value),
    [],
  );

  const validateOnChange = useValidateOnChange(validateOnChangeProp);

  const onChangeCommitted = useCallback(
    async (_: ChangeEvent, newValue: number | number[]) => {
      // We get this formik error when trying to use field.onChange - it wants
      // the corresponding name/id attribute to be populated on the Slider Thumb.
      //
      //   Warning: Formik called `handleChange`, but you forgot to pass an `id`
      //   or `name` attribute to your input:
      //
      //      <span class="MuiSlider-thumb makeStyles-thumb..."
      //            tabindex="0"
      //            role="slider">...</span>
      //
      //   Formik cannot determine which value to update. For more info see
      //   https://formik.org/docs/api/formik#handlechange-e-reactchangeeventany--void
      //
      // Using setTouched and setValue instead seems to work.
      helpers.setTouched(true, false);
      helpers.setValue(newValue, validateOnChange);

      if (validateOnChange) {
        // perform this async to allow all form changes to propagate
        await sleep(0);
      }
    },
    [helpers, validateOnChange],
  );

  return (
    <BaseSlider
      {...props}
      name={field.name}
      onChangeCommitted={onChangeCommitted}
      onChange={onChange}
      value={internalValue}
    />
  );
}
